import React, { useState, useContext, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
  Grid,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Tooltip,
  Switch,
  Chip,
  LinearProgress,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColumnFilterBuilder from "./ColumnFilterBuilder";
import { uploadColumnMedia } from "../../services/kanbanEnterpriseService";

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  colorPreview: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "2px solid rgba(0,0,0,0.1)",
    cursor: "pointer",
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.88rem",
    marginTop: theme.spacing(1),
  },
  slaGrid: {
    marginTop: theme.spacing(0.5),
  },
  slaDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: theme.spacing(0.5),
    verticalAlign: "middle",
  },
  dropZone: {
    border: "2px dashed rgba(0,0,0,0.2)",
    borderRadius: 8,
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      background: "rgba(33,150,243,0.04)",
    },
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    background: "rgba(33,150,243,0.08)",
  },
  mediaList: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  mediaChip: {
    maxWidth: 200,
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const VISIBLE_FIELDS_OPTIONS = [
  { key: "name", label: "Nome do Contato" },
  { key: "lastMessage", label: "Última Mensagem" },
  { key: "queue", label: "Fila" },
  { key: "assignee", label: "Atendente" },
  { key: "tags", label: "Tags" },
  { key: "wallet", label: "Carteira" },
  { key: "connection", label: "Conexão" },
  { key: "sla", label: "SLA Timer" },
  { key: "priority", label: "Prioridade" },
  { key: "valor", label: "Valor da Venda" },
];

const DEFAULT_VISIBLE_FIELDS = [
  "name", "lastMessage", "queue", "assignee", "sla", "tags",
];

const AUTO_ACTION_TRIGGERS = [
  { value: "on_enter", label: "Ao entrar na coluna" },
];

const AUTO_ACTION_TYPES = [
  { value: "change_status", label: "Alterar Status" },
  { value: "change_queue", label: "Alterar Fila" },
  { value: "apply_tag", label: "Aplicar Tag" },
  { value: "remove_tag", label: "Remover Tag" },
];

const STATUS_OPTIONS = [
  { value: "open", label: "Aberto" },
  { value: "pending", label: "Pendente" },
  { value: "closed", label: "Fechado" },
];

const CHAT_VISIBILITY_OPTIONS = [
  { value: "visible", label: "Sempre visível no Chat", description: "Tickets aparecem normalmente na listagem do Chat." },
  { value: "hidden_reappear", label: "Ocultar — reaparecer ao receber mensagem", description: "Tickets ficam ocultos mas reaparecem automaticamente quando o contato envia nova mensagem." },
  { value: "hidden", label: "Ocultar do Chat", description: "Tickets ficam ocultos. Não reaparecem automaticamente." },
  { value: "hidden_permanent", label: "Ocultar permanentemente", description: "Tickets nunca aparecem na listagem do Chat." },
  { value: "admin_only", label: "Visível apenas para admin/supervisor", description: "Tickets ocultos para atendentes, visíveis para admin." },
];

const COLUMN_TYPES = [
  { value: "status", label: "Por Status" },
  { value: "queue", label: "Por Fila" },
  { value: "tag", label: "Por Tag" },
  { value: "assignee", label: "Por Atendente" },
  { value: "custom", label: "Personalizado" },
];

const DEFAULT_COLORS = [
  "#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0",
  "#00BCD4", "#795548", "#607D8B", "#E91E63", "#3F51B5",
];

const ColumnEditor = ({ open, column, columns = [], onClose, onSave, onDelete }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isEditing = !!column?.id;

  const existingSettings = column?.settingsJson || {};
  const existingSla = existingSettings.sla || {};

  const [name, setName] = useState(column?.name || "");
  const [type, setType] = useState(column?.type || "custom");
  const [color, setColor] = useState(column?.color || "#757575");
  const [icon, setIcon] = useState(column?.icon || "");
  const [filterJson, setFilterJson] = useState(column?.filterJson || {});

  // SLA thresholds (minutos)
  const [slaGreen, setSlaGreen] = useState(existingSla.green ?? 15);
  const [slaYellow, setSlaYellow] = useState(existingSla.yellow ?? 30);
  const [slaOrange, setSlaOrange] = useState(existingSla.orange ?? 60);
  const [slaRed, setSlaRed] = useState(existingSla.red ?? 120);
  const [maxCards, setMaxCards] = useState(existingSettings.maxCards ?? 0);
  const [visibleFields, setVisibleFields] = useState(
    existingSettings.visibleFields || DEFAULT_VISIBLE_FIELDS
  );
  const [autoActions, setAutoActions] = useState(
    existingSettings.autoActions || []
  );
  const [chatVisibility, setChatVisibility] = useState(
    existingSettings.chatVisibility || "visible"
  );

  // Saudação da lane
  const [greetingMessage, setGreetingMessage] = useState(
    existingSettings.greetingMessage || ""
  );
  const [mediaFiles, setMediaFiles] = useState(
    existingSettings.mediaFiles || []
  );
  const [newMediaFiles, setNewMediaFiles] = useState([]);

  // Encerramento automático
  const [closeOnEnter, setCloseOnEnter] = useState(
    existingSettings.closeOnEnter || false
  );
  const [closeMessage, setCloseMessage] = useState(
    existingSettings.closeMessage || ""
  );

  // Redirecionamento automático (tempo)
  const [timeLane, setTimeLane] = useState(existingSettings.timeLane ?? 0);
  const [nextColumnId, setNextColumnId] = useState(
    existingSettings.nextColumnId || ""
  );

  // Rollback lane
  const [rollbackColumnId, setRollbackColumnId] = useState(
    existingSettings.rollbackColumnId || ""
  );

  const handleToggleField = (fieldKey) => {
    setVisibleFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((f) => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleAddAutoAction = () => {
    setAutoActions((prev) => [
      ...prev,
      { trigger: "on_enter", action: "change_status", params: { status: "open" } },
    ]);
  };

  const handleRemoveAutoAction = (idx) => {
    setAutoActions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAutoActionChange = (idx, field, value) => {
    setAutoActions((prev) =>
      prev.map((a, i) => {
        if (i !== idx) return a;
        if (field === "action") {
          // Reset params on action change
          const defaultParams =
            value === "change_status" ? { status: "open" } :
            value === "change_queue" ? { queueId: "" } :
            value === "apply_tag" ? { tagName: "" } :
            value === "remove_tag" ? { tagName: "" } : {};
          return { ...a, action: value, params: defaultParams };
        }
        if (field === "trigger") return { ...a, trigger: value };
        if (field.startsWith("params.")) {
          const paramKey = field.replace("params.", "");
          return { ...a, params: { ...a.params, [paramKey]: value } };
        }
        return a;
      })
    );
  };

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Other columns for redirect/rollback selects (exclude current column)
  const otherColumns = columns.filter((c) => c.id !== column?.id);

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    // Limit total to 5
    const remaining = 5 - mediaFiles.length - newMediaFiles.length;
    if (remaining <= 0) {
      toast.warn("Máximo de 5 arquivos de mídia por coluna.");
      return;
    }
    const toAdd = fileArray.slice(0, remaining);
    setNewMediaFiles((prev) => [...prev, ...toAdd]);
  };

  const handleDropZoneDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveExistingMedia = (idx) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveNewMedia = (idx) => {
    setNewMediaFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    // Upload new media files if any and column already exists
    let finalMediaFiles = [...mediaFiles];
    if (newMediaFiles.length > 0 && column?.id) {
      try {
        setUploading(true);
        const result = await uploadColumnMedia(column.id, newMediaFiles);
        if (result?.mediaFiles) {
          finalMediaFiles = [...finalMediaFiles, ...result.mediaFiles];
        }
      } catch (err) {
        console.error("[ColumnEditor] Media upload error:", err);
        toast.error("Erro ao enviar mídias. Tente novamente.");
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    } else if (newMediaFiles.length > 0 && !column?.id) {
      // For new columns, media will be uploaded after column is created
      // Save the file names for reference, actual upload will happen on next edit
      toast.info("Salve a coluna primeiro. Depois edite para adicionar mídias.");
    }

    onSave({
      id: column?.id,
      name: name.trim(),
      type,
      color,
      icon,
      filterJson,
      settingsJson: {
        sla: {
          green: Number(slaGreen) || 15,
          yellow: Number(slaYellow) || 30,
          orange: Number(slaOrange) || 60,
          red: Number(slaRed) || 120,
        },
        maxCards: Number(maxCards) || 0,
        visibleFields,
        chatVisibility,
        autoActions: autoActions.filter(
          (a) => a.trigger && a.action
        ),
        greetingMessage: greetingMessage.trim(),
        mediaFiles: finalMediaFiles,
        closeOnEnter,
        closeMessage: closeMessage.trim(),
        timeLane: Number(timeLane) || 0,
        nextColumnId: nextColumnId || null,
        rollbackColumnId: rollbackColumnId || null,
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? "Editar Coluna" : "Nova Coluna"}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {/* Name */}
        <TextField
          label="Nome da coluna"
          variant="outlined"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        {/* Type */}
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              // Reset filters on type change
              setFilterJson({});
            }}
            label="Tipo"
          >
            {COLUMN_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Color */}
        <div>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Cor
          </Typography>
          <div className={classes.colorRow}>
            {DEFAULT_COLORS.map((c) => (
              <div
                key={c}
                className={classes.colorPreview}
                style={{
                  backgroundColor: c,
                  borderColor: color === c ? c : "rgba(0,0,0,0.1)",
                  transform: color === c ? "scale(1.2)" : "scale(1)",
                }}
                onClick={() => setColor(c)}
              />
            ))}
            <TextField
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              size="small"
              style={{ width: 40 }}
              inputProps={{ style: { padding: 2, height: 28 } }}
            />
          </div>
        </div>

        <Divider />

        {/* Filter Builder */}
        <Typography className={classes.sectionTitle}>
          Filtros da Coluna
        </Typography>
        <ColumnFilterBuilder
          columnType={type}
          filterJson={filterJson}
          onChange={setFilterJson}
          companyId={user?.companyId}
        />

        <Divider />

        {/* SLA Config */}
        <Typography className={classes.sectionTitle}>
          Alertas de Tempo (SLA)
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Defina os limites em minutos para cada nível de alerta.
        </Typography>
        <Grid container spacing={1} className={classes.slaGrid}>
          <Grid item xs={6} sm={3}>
            <Box display="flex" alignItems="center">
              <span className={classes.slaDot} style={{ backgroundColor: "#4CAF50" }} />
              <TextField
                label="Verde"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={slaGreen}
                onChange={(e) => setSlaGreen(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="min"
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box display="flex" alignItems="center">
              <span className={classes.slaDot} style={{ backgroundColor: "#FFC107" }} />
              <TextField
                label="Amarelo"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={slaYellow}
                onChange={(e) => setSlaYellow(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="min"
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box display="flex" alignItems="center">
              <span className={classes.slaDot} style={{ backgroundColor: "#FF9800" }} />
              <TextField
                label="Laranja"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={slaOrange}
                onChange={(e) => setSlaOrange(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="min"
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box display="flex" alignItems="center">
              <span className={classes.slaDot} style={{ backgroundColor: "#F44336" }} />
              <TextField
                label="Vermelho"
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                value={slaRed}
                onChange={(e) => setSlaRed(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="min"
              />
            </Box>
          </Grid>
        </Grid>

        <Divider />

        {/* Limite de Cards */}
        <Typography className={classes.sectionTitle}>
          Limite de Cards
        </Typography>
        <TextField
          label="Máximo de tickets na coluna"
          type="number"
          variant="outlined"
          size="small"
          fullWidth
          value={maxCards}
          onChange={(e) => setMaxCards(e.target.value)}
          inputProps={{ min: 0 }}
          helperText="0 = sem limite"
        />
        <Divider />

        {/* Campos Visíveis no Card */}
        <Typography className={classes.sectionTitle}>
          Campos Visíveis no Card
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Selecione quais campos serão exibidos nos cards desta coluna.
        </Typography>
        <FormGroup row>
          {VISIBLE_FIELDS_OPTIONS.map((field) => (
            <FormControlLabel
              key={field.key}
              control={
                <Checkbox
                  size="small"
                  checked={visibleFields.includes(field.key)}
                  onChange={() => handleToggleField(field.key)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
                  {field.label}
                </Typography>
              }
            />
          ))}
        </FormGroup>

        <Divider />

        {/* Comportamento na Página de Chat */}
        <Typography className={classes.sectionTitle}>
          Comportamento na Página de Chat
        </Typography>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Visibilidade no Chat</InputLabel>
          <Select
            value={chatVisibility}
            onChange={(e) => setChatVisibility(e.target.value)}
            label="Visibilidade no Chat"
          >
            {CHAT_VISIBILITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="textSecondary">
          {CHAT_VISIBILITY_OPTIONS.find((o) => o.value === chatVisibility)?.description ||
            "Tickets desta coluna continuarão sendo gerenciados pelo Kanban."}
        </Typography>

        <Divider />

        {/* Auto-Actions (Regras por Lane) */}
        <Typography className={classes.sectionTitle}>
          Ações Automáticas
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Regras executadas automaticamente quando um ticket entra nesta coluna.
        </Typography>
        {autoActions.map((action, idx) => (
          <Box
            key={idx}
            display="flex"
            alignItems="center"
            gap={1}
            mt={0.5}
            style={{ gap: 8 }}
          >
            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <InputLabel>Gatilho</InputLabel>
              <Select
                value={action.trigger}
                onChange={(e) => handleAutoActionChange(idx, "trigger", e.target.value)}
                label="Gatilho"
              >
                {AUTO_ACTION_TRIGGERS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <InputLabel>Ação</InputLabel>
              <Select
                value={action.action}
                onChange={(e) => handleAutoActionChange(idx, "action", e.target.value)}
                label="Ação"
              >
                {AUTO_ACTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Params */}
            {action.action === "change_status" && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={action.params?.status || "open"}
                  onChange={(e) => handleAutoActionChange(idx, "params.status", e.target.value)}
                  label="Status"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {action.action === "change_queue" && (
              <TextField
                variant="outlined"
                size="small"
                label="ID da Fila"
                type="number"
                value={action.params?.queueId || ""}
                onChange={(e) => handleAutoActionChange(idx, "params.queueId", e.target.value)}
                style={{ minWidth: 100 }}
              />
            )}
            {(action.action === "apply_tag" || action.action === "remove_tag") && (
              <TextField
                variant="outlined"
                size="small"
                label="Nome da Tag"
                value={action.params?.tagName || ""}
                onChange={(e) => handleAutoActionChange(idx, "params.tagName", e.target.value)}
                style={{ minWidth: 120 }}
              />
            )}
            <Tooltip title="Remover regra">
              <IconButton size="small" onClick={() => handleRemoveAutoAction(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        <Box mt={1}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddAutoAction}
            color="primary"
          >
            Adicionar Regra
          </Button>
        </Box>

        <Divider />

        {/* ── Mensagem de Saudação da Lane ── */}
        <Typography className={classes.sectionTitle}>
          Mensagem de Saudação
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Mensagem enviada automaticamente ao cliente quando o ticket entra nesta coluna.
        </Typography>
        <TextField
          label="Mensagem de saudação"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={greetingMessage}
          onChange={(e) => setGreetingMessage(e.target.value)}
          placeholder="Ex: Olá! Seu atendimento foi encaminhado para o setor financeiro."
        />

        {/* Media Upload */}
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Mídia (imagem, áudio, vídeo, documento) — máx. 5 arquivos
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,audio/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
          style={{ display: "none" }}
          onChange={(e) => {
            handleFileSelect(e.target.files);
            e.target.value = "";
          }}
        />
        <div
          className={`${classes.dropZone} ${dragOver ? classes.dropZoneActive : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDropZoneDrop}
        >
          <UploadIcon color="action" style={{ fontSize: 32 }} />
          <Typography variant="body2" color="textSecondary">
            Clique ou arraste arquivos aqui
          </Typography>
        </div>
        {uploading && <LinearProgress style={{ marginTop: 4 }} />}

        {/* Existing media files */}
        {(mediaFiles.length > 0 || newMediaFiles.length > 0) && (
          <div className={classes.mediaList}>
            {mediaFiles.map((mf, idx) => (
              <Chip
                key={`existing-${idx}`}
                icon={<FileIcon />}
                label={mf.originalname || mf.filename || `Arquivo ${idx + 1}`}
                onDelete={() => handleRemoveExistingMedia(idx)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
                className={classes.mediaChip}
              />
            ))}
            {newMediaFiles.map((file, idx) => (
              <Chip
                key={`new-${idx}`}
                icon={<FileIcon />}
                label={file.name}
                onDelete={() => handleRemoveNewMedia(idx)}
                deleteIcon={<CloseIcon />}
                color="primary"
                variant="outlined"
                size="small"
                className={classes.mediaChip}
              />
            ))}
          </div>
        )}

        <Divider />

        {/* ── Encerramento Automático ── */}
        <Typography className={classes.sectionTitle}>
          Encerramento Automático
        </Typography>
        <div className={classes.switchRow}>
          <Typography variant="body2">
            Encerrar ticket ao entrar nesta coluna
          </Typography>
          <Switch
            checked={closeOnEnter}
            onChange={(e) => setCloseOnEnter(e.target.checked)}
            color="primary"
            size="small"
          />
        </div>
        {closeOnEnter && (
          <TextField
            label="Mensagem de encerramento"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={closeMessage}
            onChange={(e) => setCloseMessage(e.target.value)}
            placeholder="Ex: Atendimento encerrado. Obrigado pelo contato!"
            style={{ marginTop: 8 }}
          />
        )}

        <Divider />

        {/* ── Redirecionamento por Tempo ── */}
        <Typography className={classes.sectionTitle}>
          Redirecionamento Automático (Tempo)
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Move o ticket para outra coluna automaticamente após X minutos sem interação.
        </Typography>
        <Grid container spacing={1} style={{ marginTop: 4 }}>
          <Grid item xs={4}>
            <TextField
              label="Tempo (min)"
              type="number"
              variant="outlined"
              size="small"
              fullWidth
              value={timeLane}
              onChange={(e) => setTimeLane(e.target.value)}
              inputProps={{ min: 0 }}
              helperText="0 = desativado"
            />
          </Grid>
          <Grid item xs={8}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Coluna destino</InputLabel>
              <Select
                value={nextColumnId}
                onChange={(e) => setNextColumnId(e.target.value)}
                label="Coluna destino"
                disabled={!timeLane || Number(timeLane) <= 0}
              >
                <MenuItem value="">
                  <em>Nenhuma</em>
                </MenuItem>
                {otherColumns.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider />

        {/* ── Rollback Lane ── */}
        <Typography className={classes.sectionTitle}>
          Rollback (Coluna de Retorno)
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Quando o cliente responde, o ticket volta automaticamente para esta coluna.
        </Typography>
        <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 4 }}>
          <InputLabel>Coluna de retorno</InputLabel>
          <Select
            value={rollbackColumnId}
            onChange={(e) => setRollbackColumnId(e.target.value)}
            label="Coluna de retorno"
          >
            <MenuItem value="">
              <em>Nenhuma (desativado)</em>
            </MenuItem>
            {otherColumns.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        {isEditing && onDelete && (
          <Button
            onClick={() => {
              onDelete(column.id);
              onClose();
            }}
            color="secondary"
            style={{ marginRight: "auto" }}
          >
            Excluir
          </Button>
        )}
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!name.trim() || uploading}
        >
          {uploading ? "Enviando..." : isEditing ? "Salvar" : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnEditor;
